import type {
  TCLTierDefinition,
  SMOGTierDefinition,
  TCLTierName,
  SMOGTierName,
  PrescriptionRule,
  ExecuteStep,
  PartialLock,
} from '../types';

// ─── TCL Tiers ───────────────────────────────────────────────────────────────

export const tclTiers: Record<TCLTierName, TCLTierDefinition> = {
  Shield: {
    lifebarPercent: 1.0,
    color: '#4A9EFF',
    minStats: { trades: 20, winRate: 0.70 },
    description: 'Beginner-level risk management. 1.0% of account per trade.',
  },
  Sword: {
    lifebarPercent: 1.5,
    color: '#B0B8C8',
    minStats: { trades: 50, winRate: 0.73 },
    description: 'Intermediate. 1.5% of account per trade.',
  },
  Hammer: {
    lifebarPercent: 2.0,
    color: '#D4A843',
    minStats: { trades: 100, winRate: 0.76 },
    description: 'Advanced. 2.0% of account per trade.',
  },
};

// ─── SMOG Tiers ──────────────────────────────────────────────────────────────

export const smogTiers: Record<SMOGTierName, SMOGTierDefinition> = {
  Sling: {
    lifebarPercent: 6.5,
    color: '#3DBF7A',
    minStats: { trades: 20, avgWinner: 3.0, monthsTrading: 0 },
    expectancy: 0.20,
    description: 'Entry-level. 6.5% of account per trade.',
  },
  Longbow: {
    lifebarPercent: 10.0,
    color: '#E8943A',
    minStats: { trades: 12, avgWinner: 4.0, monthsTrading: 12 },
    expectancy: 0.50,
    description: 'Intermediate. 10% of account per trade.',
  },
  Crossbow: {
    lifebarPercent: 12.5,
    color: '#E84040',
    minStats: { trades: 10, avgWinner: 5.67, monthsTrading: 24 },
    expectancy: 1.0,
    description: 'Advanced. 12.5% of account per trade.',
  },
};

// ─── TCL Rules ───────────────────────────────────────────────────────────────

export function getTCLRules(dailyLossLimit: number, maxDrawdown: number): PrescriptionRule[] {
  return [
    {
      name: 'One-Shot Rule',
      description: 'Any loss ends the session. Come back tomorrow.',
      severity: 'high',
    },
    {
      name: 'Session Time Filter',
      description: 'No entries in the first 15 minutes of a session, the last 30 minutes, or within 30 minutes of a session crossover.',
      severity: 'high',
    },
    {
      name: 'Daily Loss Limit',
      description: `$${dailyLossLimit.toLocaleString()} (max 1 loss today)`,
      severity: 'high',
    },
    {
      name: 'Max Drawdown',
      description: `$${maxDrawdown.toLocaleString()} (trailing/EOD)`,
      severity: 'medium',
    },
  ];
}

// ─── SMOG Rules ──────────────────────────────────────────────────────────────

export function getSMOGRules(
  dailyLossLimit: number,
  consistencyRule: number | null,
  riskPerTrade: number,
): PrescriptionRule[] {
  const rules: PrescriptionRule[] = [
    {
      name: 'Two-Wound Rule',
      description: 'Two consecutive losses end the session. Come back tomorrow.',
      severity: 'high',
    },
    {
      name: 'Streak Shelf',
      description: 'Five losses total in a session — close the app. Return tomorrow.',
      severity: 'high',
    },
    {
      name: 'Session Time Filter',
      description: 'No entries in the first 15 minutes of a session, the last 30 minutes, or within 30 minutes of a session crossover.',
      severity: 'high',
    },
    {
      name: 'Daily Loss Limit',
      description: `$${dailyLossLimit.toLocaleString()}`,
      severity: 'high',
    },
  ];

  if (consistencyRule !== null) {
    const capR = Math.round(dailyLossLimit * consistencyRule / riskPerTrade);
    const capDollars = Math.round(dailyLossLimit * consistencyRule);
    rules.push({
      name: 'Consistency Rule',
      description: `Daily SMOG cap: $${capDollars.toLocaleString()} (${capR}R at your risk sizing). Do not exceed this today.`,
      severity: 'high',
    });
  }

  return rules;
}

// ─── TCL Execute Steps ──────────────────────────────────────────────────────

export function getTCLExecuteSteps(combinedExposure: number): ExecuteStep[] {
  return [
    { step: 1, title: 'Primary Entry', description: 'Set primary limit order at 0.382 Fib. Stop at 0.000 Fib.' },
    { step: 2, title: 'Ladder Entry', description: 'Set ladder limit at 0.17 Fib. Same stop — 0.000. Same sizing.' },
    { step: 3, title: 'Combined Exposure', description: `Both orders active simultaneously. Combined ladder exposure: $${combinedExposure.toLocaleString()}.` },
    { step: 4, title: 'Take Profit 1', description: 'At 0.618 (TP1): full exit, or close 50% if a qualifier is present.' },
    { step: 5, title: 'Runner Management', description: 'If runner active: trail stop and target 1.272 extension.' },
  ];
}

// ─── SMOG Execute Steps ─────────────────────────────────────────────────────

export const smogExecuteSteps: ExecuteStep[] = [
  { step: 1, title: 'FVG Entry', description: 'Set limit order 1 tick inside the FVG midpoint.' },
  { step: 2, title: 'Stop Placement', description: 'Set stop 1 tick beyond the candle that created the FVG.' },
  { step: 3, title: 'Breakeven Move', description: 'After a new BOS in trade direction: move stop to breakeven.' },
  { step: 4, title: 'Partial Lock', description: 'At 2R: close 50% — Partial Lock mandatory. See diagram above.' },
  { step: 5, title: 'Trail Runner', description: 'Trail remaining 50% using Heikin Ashi 15-min method until stopped out.' },
];

// ─── Partial Lock (SMOG only) ───────────────────────────────────────────────

export const smogPartialLock: PartialLock = {
  title: 'PARTIAL LOCK — MANDATORY',
  step1: 'Close 50% at 2R → Guarantees +1.0R on full position.',
  step2: 'Move remaining 50% stop to breakeven → Trail until stopped out.',
};
